import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import { usePageMeta } from "@/hooks/usePageMeta";
import Breadcrumb from "@/components/base/Breadcrumb";
import ReviewCard from "@/components/base/ReviewCard";
import ShareBar from "@/pages/detail/components/ShareBar";
import { reviews } from "@/mocks/reviews";
import { formatViews, getMovieById, getPopularReviews, getReviewById } from "@/utils/catalog";

export default function ReviewDetail() {
  const { id } = useParams();
  const review = getReviewById(id);
  const metaMovie = getMovieById(review?.movieId);

  usePageMeta(
    review
      ? `${review.title} - ${metaMovie?.title ?? "影视随笔"} - 苍井空电影`
      : "该内容已下架或不存在 - 苍井空电影大全",
    review
      ? `${review.excerpt}本文由${review.author}撰写，关联影片${metaMovie?.title ?? "影视作品"}，发布于${review.date}。`
      : "这篇影评可能已被移除或调整，可返回影评列表继续浏览。",
  );

  if (!review) {
    return (
      <SiteLayout>
        <div className="mx-auto flex w-full max-w-[1320px] flex-col items-center px-4 py-24 text-center md:px-8">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-article-line text-2xl leading-none" />
          </span>
          <h1 className="mt-5 font-heading text-xl text-foreground-950">该内容已下架或不存在</h1>
          <p className="mt-2 text-sm text-foreground-500">这篇影评可能已被移除或调整，去看看其他文章吧。</p>
          <Link
            to="/reviews"
            className="mt-6 flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-700"
          >
            <i className="ri-arrow-left-line" />
            返回影评列表
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const movie = getMovieById(review.movieId);
  const related = reviews
    .filter((item) => item.id !== review.id)
    .map((item) => {
      const sameMovie = item.movieId === review.movieId ? 3 : 0;
      const tagScore = item.tags.filter((tag) => review.tags.includes(tag)).length;
      return { item, score: sameMovie + tagScore };
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, 3)
    .map((entry) => entry.item);
  const popular = getPopularReviews(4);

  return (
    <SiteLayout>
      <div className="mx-auto w-full max-w-[1320px] px-4 py-6 md:px-8 md:py-8">
        <Breadcrumb
          items={[{ label: "影评资讯", to: "/reviews" }, { label: review.title }]}
        />

        <div className="mt-6 grid gap-8 lg:grid-cols-[1fr_300px]">
          <article className="min-w-0">
            <header className="border-b border-background-200 pb-6">
              <div className="flex flex-wrap items-center gap-2 text-[11px] text-foreground-500">
                <span className="rounded-full bg-primary-50 px-2.5 py-1 text-primary-700">影评长文</span>
                <span>{review.date}</span>
                <span className="h-3 w-px bg-background-300" />
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line text-xs" />
                  {formatViews(review.views)} 次浏览
                </span>
                <span className="h-3 w-px bg-background-300" />
                <span>{review.author}</span>
              </div>
              <h1 className="mt-3 font-heading text-2xl leading-snug text-foreground-950 md:text-[34px]">
                {review.title}
              </h1>
              <p className="mt-3 max-w-3xl text-[13px] leading-relaxed text-foreground-600">
                {review.excerpt}
              </p>

              {movie ? (
                <Link
                  to={`/detail/${movie.id}`}
                  className="mt-5 flex max-w-lg items-center gap-4 rounded-lg border border-background-200 bg-background-100/70 p-3 transition-colors hover:border-primary-300"
                >
                  <span className="block h-20 w-14 shrink-0 overflow-hidden rounded">
                    <img
                      src={movie.cover}
                      alt={`${movie.title}电影封面`}
                      title={`${movie.title} - 苍井空电影详情`}
                      className="h-full w-full object-top object-cover"
                    />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-[10px] tracking-[0.16em] text-foreground-400">RELATED FILM</span>
                    <span className="mt-1 block font-heading text-sm text-foreground-950">
                      {movie.title}
                    </span>
                    <span className="mt-0.5 block text-[11px] text-foreground-500">
                      {movie.year} · {movie.genres.join(" / ")} · 导演 {movie.director}
                    </span>
                    <span className="mt-1 inline-flex items-center gap-1 text-[11px] text-primary-700">
                      查看电影详情
                      <i className="ri-arrow-right-line" />
                    </span>
                  </span>
                </Link>
              ) : null}
            </header>

            <div className="mt-6 space-y-4">
              {review.content.map((paragraph, index) => (
                <p
                  key={index}
                  className={`text-[14px] leading-[1.95] text-foreground-800 md:text-[15px] ${
                    index === 0 ? "first-letter:float-left first-letter:mr-1 first-letter:font-heading first-letter:text-4xl first-letter:leading-[1.1] first-letter:text-primary-700" : ""
                  }`}
                >
                  {paragraph}
                </p>
              ))}
            </div>

            <div className="mt-7 flex flex-wrap items-center gap-2 border-t border-background-200 pt-5">
              <span className="text-[11px] text-foreground-500">标签</span>
              {review.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/search?q=${encodeURIComponent(tag)}`}
                  className="rounded-full bg-secondary-100 px-2.5 py-1 text-[11px] text-secondary-800 transition-colors hover:bg-secondary-200"
                >
                  {tag}
                </Link>
              ))}
              <span className="ml-auto text-[11px] text-foreground-400">作者：{review.author}</span>
            </div>

            <div className="mt-5 flex flex-col gap-3 rounded-lg border border-background-200 bg-background-100/60 p-4 sm:flex-row sm:items-center sm:justify-between">
              <Link
                to="/reviews"
                className="flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                <i className="ri-arrow-left-line" />
                返回影评列表
              </Link>
              <ShareBar title={review.title} />
            </div>
          </article>

          <aside className="space-y-4">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-base text-foreground-950">作者其他文章</h2>
              <div className="mt-2">
                {reviews
                  .filter((item) => item.author === review.author && item.id !== review.id)
                  .slice(0, 3)
                  .map((item) => (
                    <ReviewCard key={item.id} review={item} variant="mini" />
                  ))}
                {reviews.filter((item) => item.author === review.author && item.id !== review.id).length === 0 ? (
                  <p className="py-3 text-xs text-foreground-500">该作者暂无其他文章收录。</p>
                ) : null}
              </div>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-100/70 p-5">
              <h2 className="font-heading text-base text-foreground-950">热门影评</h2>
              <div className="mt-2">
                {popular.map((item) => (
                  <ReviewCard key={item.id} review={item} variant="mini" />
                ))}
              </div>
            </div>

            {movie ? (
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="font-heading text-base text-foreground-950">同作品影评</h2>
                <div className="mt-2">
                  {reviews
                    .filter((item) => item.movieId === movie.id && item.id !== review.id)
                    .map((item) => (
                      <ReviewCard key={item.id} review={item} variant="mini" />
                    ))}
                  {reviews.filter((item) => item.movieId === movie.id && item.id !== review.id).length === 0 ? (
                    <p className="py-3 text-xs text-foreground-500">这部作品目前只有这一篇影评。</p>
                  ) : null}
                </div>
              </div>
            ) : null}
          </aside>
        </div>

        <section className="mt-12 border-t border-background-200 pt-8" aria-labelledby="more-reviews">
          <h2 id="more-reviews" className="font-heading text-lg text-foreground-950">
            你可能还会喜欢
          </h2>
          <div className="mt-4 grid gap-4 md:grid-cols-3">
            {related.map((item) => (
              <ReviewCard key={item.id} review={item} />
            ))}
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}